import { useRef, useState } from 'react';
import { Loader2, Square, Users } from 'lucide-react';
import { getPressRecipients, sendPressLetterTo } from '@generated/server/pressLetters';
import { getStaffPass } from '@generated/hooks/staffPass';

const btn = 'inline-flex min-h-11 items-center gap-2 border-2 border-foreground px-4 text-sm font-semibold disabled:opacity-50';
type Recip = Awaited<ReturnType<typeof getPressRecipients>>['rows'][number];

/** Sends one letter to everyone still "Not Contacted", one at a time, marking each "Letter Sent". */
export function PressSendAll({ letterNumber, disabled, onDone }: { letterNumber: number; disabled: boolean; onDone: () => void }) {
  const [phase, setPhase] = useState<'idle' | 'loading' | 'confirm' | 'sending' | 'done'>('idle');
  const [list, setList] = useState<Recip[]>([]);
  const [info, setInfo] = useState({ other: 0, noEmail: 0 });
  const [done, setDone] = useState({ sent: 0, failed: 0, skipped: 0 });
  const [current, setCurrent] = useState('');
  const [err, setErr] = useState('');
  const stop = useRef(false);

  async function prepare() {
    setPhase('loading'); setErr('');
    try {
      const r = await getPressRecipients({ data: { pass: getStaffPass() } });
      setList(r?.rows ?? []); setInfo({ other: r?.other ?? 0, noEmail: r?.noEmail ?? 0 }); setPhase('confirm');
    } catch (e) { console.error(e); setErr('Could not load the list. Please try again.'); setPhase('idle'); }
  }

  async function sendAll() {
    setPhase('sending'); stop.current = false;
    const tally = { sent: 0, failed: 0, skipped: 0 };
    for (const r of list) {
      if (stop.current) break;
      setCurrent(`${r.contact ? `${r.contact}, ` : ''}${r.outlet}`);
      try {
        const res = await sendPressLetterTo({ data: { pass: getStaffPass(), letterNumber, rowId: r.id } });
        tally[res?.status ?? 'failed']++;
      } catch (e) {
        console.error(e);
        if (e instanceof Error && e.message.startsWith('CREDITS:')) { setErr(e.message.slice(9)); break; }
        tally.failed++;
      }
      setDone({ ...tally });
      await new Promise((ok) => setTimeout(ok, 1500)); // gentle pace so Gmail doesn't refuse
    }
    setPhase('done'); onDone();
  }

  const total = list.length;
  const progress = done.sent + done.failed + done.skipped;
  return (
    <div className="mt-4 border-t-2 border-foreground pt-4">
      {phase === 'idle' && <button type="button" onClick={prepare} disabled={disabled} className={`${btn} bg-[hsl(var(--brand-yellow))]`}><Users className="h-4 w-4" />Send letter {letterNumber} to the PR List</button>}
      {phase === 'loading' && <p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" />Counting who gets it…</p>}
      {phase === 'confirm' && (
        <div className="space-y-3">
          <p className="text-sm">Letter {letterNumber} will go to <b>{total}</b> people still marked "Not Contacted". Skipped: {info.other} already contacted (Letter Sent, Replied, Covered Us or Bad Email) and {info.noEmail} with no email. Each person is marked "Letter Sent" as it goes out. This takes about {Math.max(1, Math.round((total * 4) / 60))} minutes; keep this page open.</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={sendAll} disabled={!total} className={`${btn} bg-primary text-primary-foreground`}><Users className="h-4 w-4" />Yes, send to {total}</button>
            <button type="button" onClick={() => setPhase('idle')} className={`${btn} bg-card`}>Cancel</button>
          </div>
        </div>
      )}
      {(phase === 'sending' || phase === 'done') && (
        <div className="space-y-2">
          <div className="h-3 w-full border-2 border-foreground bg-background"><div className="h-full bg-primary transition-all" style={{ width: `${total ? (progress / total) * 100 : 0}%` }} /></div>
          <p className="text-sm">{phase === 'sending' ? <>Sending {progress + 1} of {total}: <b>{current}</b>. Keep this page open.</> : <>Finished.</>} Sent {done.sent}{done.failed ? `, failed ${done.failed} (still "Not Contacted", so sending again retries them)` : ''}{done.skipped ? `, skipped ${done.skipped}` : ''}.</p>
          {phase === 'sending' && <button type="button" onClick={() => { stop.current = true; }} className={`${btn} bg-card`}><Square className="h-4 w-4" />Stop after this one</button>}
          {phase === 'done' && <button type="button" onClick={() => { setPhase('idle'); setDone({ sent: 0, failed: 0, skipped: 0 }); }} className={`${btn} bg-card`}>Close</button>}
        </div>
      )}
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
    </div>
  );
}
